import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BACKEND_URL, getPublicCoverUrl, publicAPI } from "@/services/api";
import { wakeBackend } from "@/services/backendWake";
import { PRODUCT_TYPES, formatCurrency } from "@/utils/constants";
import ContactInfoBlock from "@/components/app/ContactInfoBlock";
import PublicContactFaqSection from "@/components/app/PublicContactFaqSection";
import { BookOpen, Users, TrendingUp, ArrowRight, Sparkles, MessageCircle, Download, LoaderCircle } from "lucide-react";

export default function LandingPage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [activeProductIndex, setActiveProductIndex] = useState(0);
  const [platformSettings, setPlatformSettings] = useState({});

  useEffect(() => {
    wakeBackend({ force: true, reason: "landing" });

    let alive = true;
    publicAPI.getCatalogue()
      .then((res) => {
        if (!alive) return;
        const products = Array.isArray(res.data) ? res.data : [];
        const withCovers = products.filter((product) => product.thumbnail_path || product.cover_image_path);
        setFeaturedProducts((withCovers.length ? withCovers : products).slice(0, 8));
      })
      .catch(() => {
        if (alive) setFeaturedProducts([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    publicAPI.getPlatformSettings()
      .then((res) => {
        if (!alive) return;
        setPlatformSettings(res.data || {});
      })
      .catch(() => {
        if (alive) setPlatformSettings({});
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (featuredProducts.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveProductIndex((index) => (index + 1) % featuredProducts.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [featuredProducts.length]);

  useEffect(() => {
    featuredProducts.slice(0, 4).forEach((product) => {
      const coverPath = product.thumbnail_path || product.cover_image_path;
      if (!coverPath) return;
      const image = new Image();
      image.decoding = "async";
      image.src = getPublicCoverUrl(coverPath);
    });
  }, [featuredProducts]);

  const activeProduct = featuredProducts[activeProductIndex] || null;
  const visibleProducts = useMemo(
    () => featuredProducts.slice(0, 4),
    [featuredProducts]
  );
  const whatsappPhone = platformSettings.whatsapp_phone || platformSettings.support_phone || "";
  const whatsappHref = buildWhatsappHref(whatsappPhone);
  const mobileAppDownloadHref = `${BACKEND_URL}/api/public/mobile-app`;

  return (
    <div className="min-h-screen bg-[#FCFBF8]">
      <header className="sticky top-0 z-50 border-b border-[#E6E4E0]/70 bg-[#FCFBF8]/90 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <h1 className="text-xl font-bold text-[#2A4B46] font-['Outfit'] sm:text-2xl" data-testid="landing-logo">
            MMJ
          </h1>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="h-10 px-3 text-sm text-[#2A4B46] sm:px-4" data-testid="login-link">
                Connexion
              </Button>
            </Link>
            <Link to="/register">
              <Button className="h-10 rounded-full bg-[#2A4B46] px-4 text-sm text-white hover:bg-[#1f3a36] sm:px-5" data-testid="register-link">
                S'inscrire
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section
        className="relative flex min-h-[calc(100svh-65px)] items-center py-12 sm:py-16 lg:py-24"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1758637612226-97ceebff95a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZXJlbmUlMjBsYW5kc2NhcGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTkwMjA1OHww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-white/60"></div>
        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6">
          <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_390px] lg:gap-12">
            <div className="max-w-3xl text-center sm:text-left">
              <h2 className="text-2xl font-bold tracking-normal text-[#2A4B46] font-['Outfit'] sm:text-3xl lg:text-4xl">
                M.M.J.
              </h2>
              <p className="mt-5 text-sm leading-7 text-[#1A1D1C] font-['Manrope'] sm:text-base">
                MMJ est une communauté numérique où les créateurs, lecteurs et partenaires découvrent, partagent, valorisent des œuvres transformée en opportunité de croissance. Rejoignez notre communauté de partenaires et bénéficiez d'un système de rémunération équitable.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full rounded-full bg-[#2A4B46] px-6 text-white hover:bg-[#1f3a36] sm:w-auto sm:px-8"
                    data-testid="hero-register-button"
                  >
                    Devenir partenaire
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/login" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full rounded-full border-[#2A4B46] bg-white/80 px-6 text-[#2A4B46] sm:w-auto sm:px-8"
                    data-testid="hero-login-button"
                  >
                    Se connecter
                  </Button>
                </Link>
              </div>
            </div>
            <CatalogueSpotlight activeProduct={activeProduct} visibleProducts={visibleProducts} activeProductIndex={activeProductIndex} setActiveProductIndex={setActiveProductIndex} />
          </div>
        </div>
      </section>

      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          <h3 className="text-center text-xl font-bold text-[#2A4B46] font-['Outfit'] sm:text-2xl lg:text-3xl">
            Pourquoi rejoindre MMJ ?
          </h3>
          <div className="mt-8 grid gap-4 sm:mt-10 sm:gap-6 lg:grid-cols-3">
            <Link to="/public/catalogue" className="rounded-xl border border-[#E6E4E0] bg-white p-5 shadow-sm transition-shadow hover:shadow-md sm:p-6">
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#2A4B46] sm:h-12 sm:w-12">
                <BookOpen className="text-white" size={23} />
              </div>
              <h4 className="text-base font-semibold text-[#2A4B46] font-['Outfit'] sm:text-lg">
                Catalogue ARTICO
              </h4>
              <p className="mt-3 text-sm leading-6 text-[#5C625F]">
                Parcourez nos textes simples, sonorisés et chantés, soigneusement sélectionnés et validés par notre
                commission.
              </p>
            </Link>

            <div className="rounded-xl border border-[#E6E4E0] bg-white p-5 shadow-sm transition-shadow hover:shadow-md sm:p-6">
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#D4A373] sm:h-12 sm:w-12">
                <Users className="text-white" size={23} />
              </div>
              <h4 className="text-base font-semibold text-[#2A4B46] font-['Outfit'] sm:text-lg">
                Réseau de partenaires
              </h4>
              <p className="mt-3 text-sm leading-6 text-[#5C625F]">
                Construisez votre équipe, parrainez de nouveaux membres et bénéficiez d'un système de niveaux
                progressif.
              </p>
            </div>

            <div className="rounded-xl border border-[#E6E4E0] bg-white p-5 shadow-sm transition-shadow hover:shadow-md sm:p-6">
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#2A4B46] sm:h-12 sm:w-12">
                <TrendingUp className="text-white" size={23} />
              </div>
              <h4 className="text-base font-semibold text-[#2A4B46] font-['Outfit'] sm:text-lg">
                Bonus multiples
              </h4>
              <p className="mt-3 text-sm leading-6 text-[#5C625F]">
                Gagnez des bonus en tant qu'auteur, démarcheur, parrain, et bien plus selon votre niveau.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#2A4B46] py-12 sm:py-16 lg:py-20">
        <div className="mx-auto w-full max-w-4xl px-4 text-center sm:px-6">
          <h3 className="text-xl font-bold text-white font-['Outfit'] sm:text-2xl lg:text-3xl">
            Prêt à commencer votre aventure ?
          </h3>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-white/90 sm:text-base">
            Rejoignez MMJ aujourd'hui et découvrez comment partager la foi tout en développant votre activité.
          </p>
          <Link to="/register" className="mt-8 inline-flex w-full sm:w-auto">
            <Button
              size="lg"
              className="w-full rounded-full bg-[#D4A373] px-6 text-white hover:bg-[#c49362] sm:w-auto sm:px-8"
              data-testid="cta-register-button"
            >
              Devenir partenaire maintenant
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <PublicContactFaqSection platformSettings={platformSettings} />

      <footer className="border-t border-[#E6E4E0] bg-white py-8 sm:py-10">
        <div className="mx-auto w-full max-w-6xl space-y-5 px-4 text-center sm:px-6">
          <ContactInfoBlock compact />
          <a
            href={mobileAppDownloadHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2A4B46] transition-colors hover:text-[#7A6250]"
            data-testid="landing-download-app-footer"
          >
            <Download size={17} />
            Télécharger l'application mobile Android
          </a>
          <p className="text-xs text-[#5C625F] sm:text-sm">
            (c)2025 Artico-MMJ. Tous droits réservés
          </p>
        </div>
      </footer>

      <a
        href={mobileAppDownloadHref}
        target="_blank"
        rel="noreferrer"
        className={`fixed right-5 z-50 flex items-center gap-3 rounded-full border border-[#D7E7DE] bg-white px-4 py-3 text-[#2A4B46] shadow-lg shadow-black/10 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:right-6 ${whatsappHref ? "bottom-24 sm:bottom-28" : "bottom-5 sm:bottom-6"}`}
        aria-label="Télécharger l'application mobile MMJ pour Android"
        data-testid="landing-download-app-bubble"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2A4B46] text-white">
          <Download size={20} />
        </span>
        <span className="hidden text-left sm:block">
          <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#7A6250]">Android</span>
          <span className="block text-sm font-semibold text-[#2A4B46]">Télécharger MMJ</span>
        </span>
      </a>

      {whatsappHref ? (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-3 rounded-full border border-[#D7E7DE] bg-white px-4 py-3 text-[#2A4B46] shadow-lg shadow-black/10 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:bottom-6 sm:right-6"
          aria-label="Contacter Artico sur WhatsApp"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white">
            <MessageCircle size={20} />
          </span>
          <span className="hidden text-left sm:block">
            <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#7A6250]">WhatsApp</span>
            <span className="block text-sm font-semibold text-[#2A4B46]">{platformSettings.whatsapp_phone || platformSettings.support_phone || "Contact rapide"}</span>
          </span>
        </a>
      ) : null}
    </div>
  );
}

function buildWhatsappHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return `https://wa.me/${digits}`;
}

function CatalogueSpotlight({ activeProduct, visibleProducts, activeProductIndex, setActiveProductIndex }) {
  const [displayProduct, setDisplayProduct] = useState(activeProduct);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!activeProduct) {
      setDisplayProduct(null);
      setIsTransitioning(false);
      return undefined;
    }

    if (!displayProduct || displayProduct.id === activeProduct.id) {
      setDisplayProduct(activeProduct);
      setIsTransitioning(false);
      return undefined;
    }

    setIsTransitioning(true);
    const coverPath = activeProduct.thumbnail_path || activeProduct.cover_image_path;
    const coverUrl = coverPath ? getPublicCoverUrl(coverPath) : null;
    const image = coverUrl ? new Image() : null;
    let cancelled = false;
    const commitProduct = () => {
      if (cancelled) return;
      setDisplayProduct(activeProduct);
      window.requestAnimationFrame(() => setIsTransitioning(false));
    };

    if (!image) {
      commitProduct();
    } else {
      image.decoding = "async";
      image.onload = commitProduct;
      image.onerror = commitProduct;
      image.src = coverUrl;
    }

    return () => {
      cancelled = true;
      if (image) {
        image.onload = null;
        image.onerror = null;
      }
    };
  }, [activeProduct, displayProduct]);

  if (!displayProduct) {
    return (
      <Link
        to="/public/catalogue"
        className="group block overflow-hidden rounded-[1.5rem] border border-white/70 bg-white/80 p-5 text-left shadow-xl shadow-[#2A4B46]/10 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
      >
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#2A4B46]">
          <Sparkles size={16} />
          Catalogue en vitrine
        </div>
        <div className="mt-5 aspect-[3/4] overflow-hidden rounded-[1.25rem] bg-[#2A4B46] p-6 text-white">
          <div className="flex h-full flex-col justify-end">
            <p className="max-w-[10ch] text-2xl font-extrabold leading-[1.02] font-['Outfit']">
              Explorez ARTICO
            </p>
            <p className="mt-3 text-sm leading-6 text-white/80">
              Découvrez les œuvres disponibles dans le catalogue MMJ.
            </p>
          </div>
        </div>
        <span className="mt-5 inline-flex items-center text-sm font-bold text-[#2A4B46]">
          Voir le catalogue <ArrowRight className="ml-2" size={17} />
        </span>
      </Link>
    );
  }

  const coverPath = displayProduct.thumbnail_path || displayProduct.cover_image_path;
  const coverUrl = coverPath ? getPublicCoverUrl(coverPath) : null;
  const productUrl = `/public/catalogue?product=${encodeURIComponent(displayProduct.id)}`;
  const authorName = displayProduct.author_name || displayProduct.author || displayProduct.created_by_name || "Auteur MMJ";
  const isFree = displayProduct.is_free_for_active_par || Number(displayProduct.price_mc || 0) <= 0;

  return (
    <div className="w-full">
      <Link
        to={productUrl}
        className={`group block overflow-hidden rounded-[1.75rem] border border-white/70 bg-white/88 text-left shadow-[0_22px_60px_-25px_rgba(42,75,70,0.45)] backdrop-blur-xl transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-[0_28px_72px_-24px_rgba(42,75,70,0.55)] ${isTransitioning ? "opacity-80" : "opacity-100"}`}
        data-testid="landing-featured-product"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#263A36]">
          <div className={`absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent transition-opacity duration-700 ${isTransitioning ? "opacity-85" : "opacity-100"}`} />
          {coverUrl ? (
            <img
              key={displayProduct.id}
              src={coverUrl}
              alt={displayProduct.title}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className={`h-full w-full object-cover transition-all duration-700 ease-out ${isTransitioning ? "scale-[1.04] blur-[1px]" : "scale-100 blur-0"} group-hover:scale-[1.06]`}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-8 text-center text-2xl font-bold text-white">
              {displayProduct.title}
            </div>
          )}
          {isTransitioning && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#263A36]/90" aria-label="Chargement de la couverture">
              <LoaderCircle className="animate-spin text-white/85" size={34} aria-hidden="true" />
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
            <div className="rounded-[1.25rem] border border-white/15 bg-black/35 p-4 text-white backdrop-blur-md sm:p-5">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge className="border border-white/15 bg-white/15 text-white">{PRODUCT_TYPES[displayProduct.product_type]?.label || "Œuvre"}</Badge>
                <Badge className="bg-[#D4A373] text-white">{isFree ? "Libre d'accès" : formatCurrency(displayProduct.price_mc)}</Badge>
              </div>
              <p className="text-[1.35rem] font-extrabold leading-tight tracking-normal font-['Outfit'] sm:text-[1.75rem]">
                {displayProduct.title}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-white/75">
                {authorName}
              </p>
              <p className="mt-3 line-clamp-3 text-sm leading-6 text-white/90">
                {displayProduct.summary || displayProduct.description || "Une œuvre à découvrir dans le catalogue MMJ."}
              </p>
              <span className="mt-4 inline-flex items-center text-sm font-bold text-white">
                Consulter cette œuvre <ArrowRight className="ml-2" size={17} />
              </span>
            </div>
          </div>
        </div>
      </Link>

      {visibleProducts.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2 lg:justify-start">
          {visibleProducts.map((product, index) => (
            <button
              key={product.id || index}
              type="button"
              onClick={() => setActiveProductIndex(index)}
              className={`h-2.5 rounded-full transition-all ${index === activeProductIndex ? "w-8 bg-[#2A4B46]" : "w-2.5 bg-[#2A4B46]/25 hover:bg-[#2A4B46]/50"}`}
              aria-label={`Afficher ${product.title}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
